import * as React from 'react'

import { Popover as PopoverPrimitive } from '@base-ui/react/popover'

import { cn } from '@/lib/utils'

function Popover({ ...props }: PopoverPrimitive.Root.Props): JSX.Element {
  return <PopoverPrimitive.Root data-slot='popover' {...props} />
}

function PopoverTrigger({
  ...props
}: PopoverPrimitive.Trigger.Props): JSX.Element {
  return <PopoverPrimitive.Trigger data-slot='popover-trigger' {...props} />
}

function PopoverContent({
  className,
  positionerClassName,
  align = 'center',
  alignOffset = 0,
  anchor,
  collisionAvoidance,
  container,
  side = 'bottom',
  sideOffset = 4,
  ...props
}: PopoverPrimitive.Popup.Props &
  Pick<
    PopoverPrimitive.Positioner.Props,
    | 'align'
    | 'alignOffset'
    | 'anchor'
    | 'collisionAvoidance'
    | 'side'
    | 'sideOffset'
  > &
  Pick<PopoverPrimitive.Portal.Props, 'container'> & {
    positionerClassName?: string
  }): JSX.Element {
  return (
    // Portal target. Body by default, as before — pass a `container` inside a
    // scroll box to make the popup scroll and clip WITH its anchor instead of
    // floating over the rest of the page.
    <PopoverPrimitive.Portal container={container}>
      <PopoverPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        // Position against something other than the trigger. Needed by
        // popovers that are opened programmatically and have no trigger of
        // their own; omitted, Base UI falls back to the trigger as before.
        anchor={anchor}
        // Opt out of flip/shift for popups that must stay rigidly on one side
        // of their anchor; omitted, Base UI's defaults apply as before.
        collisionAvoidance={collisionAvoidance}
        side={side}
        sideOffset={sideOffset}
        className={cn('isolate z-50', positionerClassName)}
      >
        <PopoverPrimitive.Popup
          data-slot='popover-content'
          className={cn(
            'bg-popover text-popover-foreground data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=inline-end]:slide-in-from-left-2 origin-(--transform-origin) outline-hidden z-50 flex w-72 flex-col gap-2.5 rounded-lg p-2.5 text-sm shadow-md duration-100',
            className
          )}
          {...props}
        />
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  )
}

// Base UI places it against the anchor, so it keeps pointing at the anchor
// when collision avoidance shifts the popup along its side.
function PopoverArrow({
  className,
  ...props
}: PopoverPrimitive.Arrow.Props): JSX.Element {
  return (
    <PopoverPrimitive.Arrow
      data-slot='popover-arrow'
      className={cn(
        'size-2.5 rotate-45 rounded-[2px] data-[side=bottom]:-top-1 data-[side=left]:-right-1 data-[side=right]:-left-1 data-[side=top]:-bottom-1',
        className
      )}
      {...props}
    />
  )
}

function PopoverHeader({
  className,
  ...props
}: React.ComponentProps<'div'>): JSX.Element {
  return (
    <div
      data-slot='popover-header'
      className={cn('flex flex-col gap-0.5 text-sm', className)}
      {...props}
    />
  )
}

function PopoverTitle({
  className,
  ...props
}: PopoverPrimitive.Title.Props): JSX.Element {
  return (
    <PopoverPrimitive.Title
      data-slot='popover-title'
      className={cn('font-medium', className)}
      {...props}
    />
  )
}

function PopoverDescription({
  className,
  ...props
}: PopoverPrimitive.Description.Props): JSX.Element {
  return (
    <PopoverPrimitive.Description
      data-slot='popover-description'
      className={cn('text-muted-foreground', className)}
      {...props}
    />
  )
}

export {
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger
}
